import { Shield, Users, Award, Building2, Plane } from "lucide-react";
import tamaraLogo from "@/assets/logos/tamara-logo.png";
import { COPY } from "@/content/copy";

const partners: Array<
  | { type: "logo"; name: string; logo: string }
  | { type: "placeholder"; name: string; icon: "bank" | "travel"; subtitle: string }
> = [
  { type: "logo", name: "Tabby", logo: "/lovable-uploads/9fad4e46-5b8f-4191-90ff-40dfdee792b8.png" },
  { type: "logo", name: "Tamara", logo: tamaraLogo },
  { type: "placeholder", name: "Bank Partners", icon: "bank", subtitle: "Live Q2 2026" },
  { type: "placeholder", name: "Travel Partners", icon: "travel", subtitle: "Live Q2 2026" },
];

const CardBase = ({ children, dashed = false }: { children: React.ReactNode; dashed?: boolean }) => (
  <div
    className={`group flex items-center justify-center bg-white rounded-2xl p-6 transition-transform duration-200 hover:scale-[1.02] focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 h-[140px] overflow-hidden ${
      dashed ? "border-2 border-dashed border-muted-foreground/30" : "shadow-sm hover:shadow-md border border-border"
    }`}
    role="img"
  >
    {children}
  </div>
);

const PartnerLogo = ({ name, logo }: { name: string; logo: string }) => (
  <CardBase>
    <img
      src={logo}
      alt={`${name} official logo`}
      className={`object-contain transition w-[70%] max-h-[60px] ${
        name === "Tamara" ? "mix-blend-multiply" : ""
      }`}
      loading="lazy"
      style={{ filter: "brightness(0.95)" }}
    />
  </CardBase>
);

const PlaceholderCard = ({ name, icon, subtitle }: { name: string; icon: "bank" | "travel"; subtitle: string }) => (
  <CardBase dashed>
    <div className="flex flex-col items-center justify-center text-center">
      <div
        className="mb-2 inline-flex items-center justify-center rounded-xl w-10 h-10 bg-primary/10 text-primary"
        aria-hidden="true"
      >
        {icon === "bank" ? <Building2 className="w-5 h-5" /> : <Plane className="w-5 h-5" />}
      </div>
      <div className="text-sm font-medium text-foreground">{name}</div>
      <div className="text-xs text-muted-foreground">{subtitle}</div>
    </div>
  </CardBase>
);

const TrustSection = () => {
  return (
    <section className="py-20 px-4 bg-primary/5" id="partners" data-section="partners">
      <div className="max-w-6xl mx-auto text-center">
        <div className="mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold mb-3 text-primary">
            {COPY.trustSection.title}
          </h2>
          <div
            className="mx-auto mb-6 h-1 w-24 rounded-full bg-primary"
            aria-hidden="true"
          />
          <p className="text-lg md:text-xl text-muted-foreground font-body max-w-3xl mx-auto leading-relaxed">
            We work with leading BNPL providers and banks to make financing safe, secure, and stress-free.
          </p>
        </div>

        {/* Partner Logos / Placeholders */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12 max-w-4xl mx-auto">
          {partners.map((p, i) =>
            p.type === "logo" ? (
              <PartnerLogo key={`${p.name}-${i}`} name={p.name} logo={p.logo} />
            ) : (
              <PlaceholderCard key={`${p.name}-${i}`} name={p.name} icon={p.icon} subtitle={p.subtitle} />
            )
          )}
        </div>

        <div className="text-center mb-8">
          <p className="text-muted-foreground font-body">{COPY.trustSection.subtitle}</p>
        </div>

        {/* Security / Trust Badges */}
        <div className="flex flex-wrap justify-center items-center gap-6">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-body font-medium">SSL Secured</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="w-5 h-5 text-accent" />
            <span className="font-body font-medium">Backed by licensed partners</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Award className="w-5 h-5 text-primary" />
            <span className="font-body font-medium">Powered by certified providers</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
